import { HttpErrorResponse, HttpEvent, HttpHandler, HttpRequest } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Router } from '@angular/router';
import { Observable, throwError } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { LoginService } from '@services/login/login.service';
import { SnackBarService } from '@services/snackbar/snack-bar-service.service';
import { DiagnosticoService } from '@services/diagnostico/diagnostico.service';

@Injectable({
  providedIn: 'root'
})
export class GlobalHttpErrorInterceptorService {
  private loginService = inject(LoginService);
  private router = inject(Router);
  private snackBar = inject(SnackBarService);
  private diagnostico = inject(DiagnosticoService);

  private retryAttempts = 1;

  intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    return next.handle(req).pipe(
      // retry(this.retryAttempts),
      catchError((error) => this.handleError(error, req))
    );
  }



  // Se agrega "req" para que DiagnosticoService pueda registrar qué request falló
  // (método + url + status) como breadcrumb, y así el módulo de Feedback ("Reportar un
  // problema") tenga contexto técnico real de los últimos errores de red sin que el
  // usuario tenga que describirlos.
  private handleError(error: HttpErrorResponse, req: HttpRequest<any>): Observable<never> {
    this.diagnostico.registrarErrorHttp(req, error);

    let errorMessage = '';

    // Manejo de errores específicos por código de estado
    switch (error.status) {
      case 400:
        errorMessage = 'El servidor no puede o no procesará la petición debido a un error.';
        break;
      case 401:
        // Los endpoints de autenticación devuelven 401 como respuesta esperada: credenciales
        // incorrectas en el Login, o refresh token vencido cuando el AuthGuard intenta renovar
        // la sesión al abrir la app. Desloguear acá pisaba ese flujo (y borraba el refresh token
        // antes de que el AuthInterceptor pudiera usarlo), así que se deja que el componente o
        // el guard que hizo la llamada decida qué hacer.
        if (this.esEndpointDeAutenticacion(req.url)) {
          errorMessage = 'No se pudo validar la sesión.';
          break;
        }

        this.loginService.logout();
        this.router.navigateByUrl('/login');
        errorMessage = 'Es necesario autenticar para obtener la respuesta solicitada.';
        break;
      case 403:
        // Auditoría QA (2026-08-10): antes deslogueaba igual que un 401. Un 403 significa
        // "estás autenticado pero no tenés permiso para ESTA acción puntual" (ver
        // AuthorizeAttribute en la API) — no que la sesión sea inválida. Desloguear acá tiraba
        // al usuario fuera de toda la app por un solo request sin permiso (ej. un Administrador
        // de Empresa pegándole a un endpoint restringido a SuperAdministrador), incluso en medio
        // de llamadas en paralelo que sí eran legítimas.
        errorMessage = 'No tenés permiso para realizar esta acción.';
        break;
      case 404:
        errorMessage = 'El servidor no pudo encontrar el contenido solicitado.';
        break;
      case 409:
        errorMessage = 'El registro fue modificado por otro usuario después de que lo cargaste. Recargá los datos y volvé a intentar.';
        break;
      case 422:
        errorMessage = 'Los datos enviados no son válidos.';
        break;
      case 500:
        errorMessage = 'El servidor ha encontrado una situación que no sabe cómo manejarla.';
        break;
      case 503:
        errorMessage = 'El servidor no está listo para manejar la petición. Podría estar caído por mantenimiento o sobrecargado.';
        break;
      case 504:
        errorMessage = 'Error de conexión. El servidor no pudo obtener una respuesta a tiempo.';
        break;
      default:
        errorMessage = 'Ups... hubo un problema en el sistema.';
        break;
    }

    if (error.error) {
      const erroresDeValidacion = this.extraerErroresDeValidacion(error.error.errors);
      errorMessage = erroresDeValidacion || error.error.detail || error.error.failureReason || errorMessage;

      errorMessage = this.mapCommonErrors(errorMessage);
    }

    this.snackBar.showError(errorMessage,'Atención');

    return throwError(() => error);
  }

  private extraerErroresDeValidacion(errors: Record<string, string[]> | undefined | null): string | null {
    if (!errors || typeof errors !== 'object') {
      return null;
    }

    const mensajes = Object.values(errors)
      .filter((arr): arr is string[] => Array.isArray(arr))
      .flat()
      .filter(Boolean);

    return mensajes.length > 0 ? mensajes.join(' ') : null;
  }

  // Mapeo de errores comunes
  private mapCommonErrors(message: string): string {
    if (message.includes("REFERENCE 'FK")) return "El registro no se puede eliminar debido a referencias asociadas.";
    if (message.includes("Http failure response")) return "Error de conexión.";
    if (message.includes("A connection attempt failed")) return "La conexión falló porque la parte conectada no respondió.";
    if (message.includes("Sequence contains no elements")) return "La consulta no contiene elementos.";
    if (message.includes("Year, Month, and Day parameters describe an un-representable DateTime.")) return "Fecha no representable.";
    if (message.includes("Execution Timeout Expired")) return "Tiempo de espera expirado.";
    if (message.includes("An error occurred while updating the entries.")) return "Error al actualizar las entradas.";

    return message;
  }

  private readonly URLS_AUTENTICACION = [
    '/api/Auth/Login',
    '/api/Auth/RefreshToken',
    '/api/Auth/RefreshTokenWithCompany',
    '/api/Auth/GoogleLogin',
    '/api/Auth/GoogleRegister',
    '/api/Auth/MicrosoftLogin',
    '/api/Auth/MicrosoftRegister',
  ];

  private esEndpointDeAutenticacion(url: string): boolean {
    const path = (url || '').split('?')[0];
    return this.URLS_AUTENTICACION.some(u => path.endsWith(u) || path.includes(u));
  }

}
